<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'Modal',
})
</script>
<template>
  <!-- Modal 根节点：遮罩 + 内容容器 -->
  <div
    class="mod-modal"
    :class="{ 'with-slot-header': $slots['header-img'] }"
    :style="{ backgroundColor: `rgba(0, 0, 0, ${maskOpacity})` }"
    v-if="isModalShow"
    @click.stop="clickMask"
  >
    <div class="content-wrapper" :style="{ borderRadius: borderRadius }" @click.stop>
      <!-- 可自定义 header 图片插槽 -->
      <div class="header-img" v-if="$slots['header-img']">
        <slot name="header-img"></slot>
      </div>

      <!-- title 插槽 / 默认 title 文案 -->
      <slot name="title">
        <h3 v-if="title" class="title">{{ title }}</h3>
      </slot>

      <!-- content 插槽 / 默认内容文案 -->
      <slot name="content">
        <p v-if="content" class="content-text" :class="contentAlign" v-html="content"></p>
      </slot>

      <!-- 底部按钮组：支持单按钮或双按钮 -->
      <div class="btn-groups" :class="{ 'single-button': !showCancelButton }">
        <button class="btn-item" :style="{ color: cancelTextColor, background: cancelBgColor }" v-if="showCancelButton" @click.stop="cancel">
          {{ cancelText }}
        </button>
        <button class="btn-item" :style="{ color: confirmTextColor, background: confirmBgColor }" @click.stop="confirm">
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup name="Modal" lang="ts">
// imports
import { ref, onBeforeUnmount, onDeactivated } from "vue";

// 定义参数类型接口：showModal 时传入的配置项
export interface ModalOptions {
  image?: string;
  title?: string;
  content?: string;
  cancelText?: string;
  confirmText?: string;
  contentAlign?: "left" | "center";
  cancelTextColor?: string;
  confirmTextColor?: string;
  cancelBgColor?: string;
  confirmBgColor?: string;
  isMaskBindEvent?: boolean;
  showCancelButton?: boolean;
  borderRadius?: string;
  maskOpacity?: number; // 添加 mask 透明度配置
  onCancel?: () => void;
  onConfirm?: () => void;
  onerror?: () => void;
}

// 默认值类型：内部默认弹窗状态与样式配置
interface DefaultModalValues {
  isModalShow: boolean;
  title: string;
  content: string;
  cancelText: string;
  confirmText: string;
  cancelTextColor: string;
  confirmTextColor: string;
  cancelBgColor: string;
  confirmBgColor: string;
  isMaskBindEvent: boolean;
  showCancelButton: boolean;
  contentAlign: "left" | "center"; // 新增：默认对齐方式
  borderRadius?: string;
  maskOpacity: number; // 添加默认透明度
}

// 回调函数类型
type CallbackFunction = (() => void) | null;

// refs：组件内部显示状态与配置值
const isModalShow = ref<boolean>(false);
const title = ref<string>("");
const content = ref<string>("");
const cancelText = ref<string>("取消");
const confirmText = ref<string>("确认");
const cancelTextColor = ref<string>("#2ED15F");
const confirmTextColor = ref<string>("#fff");
const cancelBgColor = ref<string>("#E5FFEA");
const confirmBgColor = ref<string>("#2ED15F");
const isMaskBindEvent = ref<boolean>(false);
const showCancelButton = ref<boolean>(true);
const contentAlign = ref<"left" | "center">("center");
const borderRadius = ref<string>("0.56rem");
const maskOpacity = ref<number>(0.5); // 添加 mask 透明度变量，默认0.5
let onCancel: CallbackFunction = null;
let onConfirm: CallbackFunction = null;

// body overflow 控制函数，避免多处重复访问 DOM
const setBodyOverflow = (value: string): void => {
  document.body.style.overflow = value;
};

// Default values for modal properties
const defaultModal: DefaultModalValues = {
  isModalShow: false,
  title: "",
  content: "",
  cancelText: "取消",
  confirmText: "确认",
  cancelTextColor: "#2ED15F",
  confirmTextColor: "#fff",
  cancelBgColor: "#E5FFEA",
  confirmBgColor: "#2ED15F",
  borderRadius: "0.56rem",
  isMaskBindEvent: false,
  showCancelButton: true,
  contentAlign: "center", // 新增：默认居中
  maskOpacity: 0.5, // 添加默认透明度值
};

// methods
// 重置 modal 配置到默认值，保证每次 showModal 时状态一致
const resetToDefault = (): void => {
  isModalShow.value = defaultModal.isModalShow;
  title.value = defaultModal.title;
  content.value = defaultModal.content;
  cancelText.value = defaultModal.cancelText;
  confirmText.value = defaultModal.confirmText;
  cancelTextColor.value = defaultModal.cancelTextColor;
  confirmTextColor.value = defaultModal.confirmTextColor;
  cancelBgColor.value = defaultModal.cancelBgColor;
  confirmBgColor.value = defaultModal.confirmBgColor;
  borderRadius.value = defaultModal.borderRadius;
  isMaskBindEvent.value = defaultModal.isMaskBindEvent;
  showCancelButton.value = defaultModal.showCancelButton;
  contentAlign.value = defaultModal.contentAlign;
  maskOpacity.value = defaultModal.maskOpacity; // 重置 mask 透明度
};

// 通用 ref 值设置器，仅在参数不为 undefined 时才更新目标 ref
const setRefValue = <T,>(refObject: { value: T }, value: T | undefined): void => {
  if (value !== undefined) refObject.value = value;
};

// 显示 Modal，并根据传入参数覆盖默认配置
const showModal = (params: ModalOptions = {}): void => {
  setBodyOverflow("hidden");
  resetToDefault();
  isModalShow.value = true;

  // Update properties from params
  setRefValue(title, params.title);
  setRefValue(content, params.content);
  setRefValue(cancelText, params.cancelText);
  setRefValue(confirmText, params.confirmText);
  setRefValue(cancelTextColor, params.cancelTextColor);
  setRefValue(confirmTextColor, params.confirmTextColor);
  setRefValue(cancelBgColor, params.cancelBgColor);
  setRefValue(confirmBgColor, params.confirmBgColor);
  setRefValue(borderRadius, params.borderRadius);
  setRefValue(isMaskBindEvent, params.isMaskBindEvent);
  setRefValue(showCancelButton, params.showCancelButton);
  setRefValue(contentAlign, params.contentAlign);
  setRefValue(maskOpacity, params.maskOpacity); // 设置 mask 透明度

  // Set callback functions explicitly, undefined resets to null
  onCancel = params.onCancel || null;
  onConfirm = params.onConfirm || null;
};

// 隐藏 Modal，并清理回调函数与遮罩滚动控制
const hideModal = (): void => {
  isModalShow.value = false;
  onCancel = null;
  onConfirm = null;
  setBodyOverflow("auto");
};

// 取消按钮点击处理：恢复滚动，隐藏 Modal，并触发回调
const cancel = (): void => {
  setBodyOverflow("auto");
  isModalShow.value = false;
  if (onCancel) onCancel();
};

// 确认按钮点击处理：恢复滚动，隐藏 Modal，并触发回调
const confirm = (): void => {
  setBodyOverflow("auto");
  isModalShow.value = false;
  if (onConfirm) onConfirm();
};

// 点击遮罩层处理
// 仅当 isMaskBindEvent 为 true 时触发 cancel
const clickMask = (): void => {
  if (!isMaskBindEvent.value) return;
  cancel();
};

// Expose methods to be used by parent components
defineExpose({
  showModal,
  hideModal,
});

// 生命周期函数：组件卸载时恢复 body 滚动状态
onBeforeUnmount(() => {
  setBodyOverflow("");
});

// 生命周期函数：组件被停用时恢复 body 滚动状态，防止页面切换后页面无法滚动
onDeactivated(() => {
  setBodyOverflow("");
});
</script>

<style lang="less" scoped>
@import "./Modal.less";
</style>
